import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdOutlineAdd } from 'react-icons/md';
import { Button, SearchInput } from '@ds';
import { AdminPageHeader, ConditionalRender, ConfirmationModal, DataTable, DeleteModal } from '@ui';
import { AuthContext } from '@lp/core/auth';
import {
  OCS_CONSOLES_LIST_FILTERS_BASE,
  getRenderOcsConsolesTableCell,
  OCS_CONSOLES_TABLE_COLUMNS,
  OcsConsolesFilters,
} from '@lp/features/admin/tables/OcsConsolesListTable';
import { OcsConsoleData } from '@/api/apis/resources';
import {
  useOcsConsolesListQuery,
  useRemoveOcsConsoleMutation,
  useUpdateOcsConsoleMutation,
} from '@/queries/ocs-consoles';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { AdminPermissions } from '@/permissions';
import { useTrialProgramsList } from '@/hooks/useTrialProgramsList';

export const OcsConsolesList = () => {
  const [ocsConsoleToChange, setOcsConsoleToChange] = useState<OcsConsoleData | undefined>();
  const [confirmationModalTitle, setConfirmationModalTitle] = useState<string>('');
  const [confirmationModalDesc, setConfirmationModalDesc] = useState<string>('');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const { mutateAsync } = useUpdateOcsConsoleMutation();
  const navigate = useNavigate();
  const { checkPermission } = useContext(AuthContext);
  const { getTrialProgramByOrgan } = useTrialProgramsList();

  const hasCreatePermissions = checkPermission(AdminPermissions.OcsConsolesCreate);
  const hasEditPermissions = checkPermission(AdminPermissions.OcsConsolesUpdate);
  const hasDeletePermissions = checkPermission(AdminPermissions.OcsConsolesDelete);

  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: OCS_CONSOLES_LIST_FILTERS_BASE,
    query: useOcsConsolesListQuery,
  });

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useRemoveOcsConsoleMutation,
    successMessage: 'OCS Console deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const onIsActiveSwitchChanged = useCallback((ocsConsole: OcsConsoleData) => {
    setConfirmationModalTitle('Change OCS Console maintenance status?');
    setConfirmationModalDesc('Are you sure you want to change the maintenance status?');
    setOcsConsoleToChange(ocsConsole);
  }, []);

  const onToggleTrialProgram = useCallback(
    async (console: OcsConsoleData) => {
      await mutateAsync({
        id: console.id,
        trialProgramId: console.trialProgram ? null : getTrialProgramByOrgan(console.organType)?.id,
      });
    },
    [mutateAsync, getTrialProgramByOrgan],
  );

  useEffect(() => {
    if (ocsConsoleToChange) {
      setConfirmationModalOpen(true);
    }
  }, [ocsConsoleToChange]);

  const onChangeActive = async () => {
    if (ocsConsoleToChange) {
      await mutateAsync({
        id: ocsConsoleToChange?.id,
        isInMaintenance: !ocsConsoleToChange?.isInMaintenance,
      });
    }
  };

  const onConfirmationModalClose = useCallback(() => {
    setOcsConsoleToChange(undefined);
    setConfirmationModalOpen(false);
  }, []);

  const handleCreate = () => {
    navigate('/admin/devices/ocs_consoles/create');
  };

  const handleEdit = useCallback(
    (consoleId: string) => {
      navigate(`/admin/devices/ocs_consoles/${consoleId}`);
    },
    [navigate],
  );

  const renderConsolesTableCell = useMemo(() => {
    return getRenderOcsConsolesTableCell(
      handleEdit,
      onModalOpen,
      onIsActiveSwitchChanged,
      onToggleTrialProgram,
      hasCreatePermissions,
      hasEditPermissions,
      hasDeletePermissions,
    );
  }, [
    handleEdit,
    onModalOpen,
    onIsActiveSwitchChanged,
    onToggleTrialProgram,
    hasCreatePermissions,
    hasEditPermissions,
    hasDeletePermissions,
  ]);

  const OCS_CONSOLES_TABLE_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions || hasEditPermissions || hasDeletePermissions
      ? OCS_CONSOLES_TABLE_COLUMNS
      : OCS_CONSOLES_TABLE_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions, hasDeletePermissions, hasEditPermissions]);

  return (
    <div className="flex flex-col place-items-stretch gap-9">
      <AdminPageHeader title="OCS Consoles">
        <div className="max-w-[480px] grow">
          <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
        </div>
        <ConditionalRender show={hasCreatePermissions}>
          <div className="shrink-0">
            <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onClick={handleCreate}>
              Create
            </Button>
          </div>
        </ConditionalRender>
      </AdminPageHeader>
      <div className="flex flex-col gap-4">
        <OcsConsolesFilters
          filters={filters}
          hasFilters={hasFilters}
          updateFilters={updateFilters}
          clearFilters={clearFilters}
        />
        <DataTable
          label="OCS Consoles"
          columns={OCS_CONSOLES_TABLE_COLUMNS_FILTERED}
          renderCell={renderConsolesTableCell}
          data={data?.items || []}
          isLoading={isLoading}
          isFetching={isFetching}
          totalItems={data?.totalItems || 0}
          totalPages={data?.totalPages || 1}
          page={page}
          itemsPerPage={itemsPerPage}
          sortBy={sortBy}
          onPageChange={setPage}
          onItemsPerPageChange={setItemsPerPage}
          onSortByChange={setSortBy}
        />
      </div>
      <ConfirmationModal
        title={confirmationModalTitle}
        description={confirmationModalDesc}
        isOpen={confirmationModalOpen}
        onChangeActive={onChangeActive}
        onClose={onConfirmationModalClose}
      />
      {hasDeletePermissions && (
        <DeleteModal
          title="Delete OCS Console?"
          description="Are you sure you want to delete the OCS Console?"
          isOpen={isOpen}
          onDelete={onEntityDelete}
          onClose={onModalClose}
        />
      )}
    </div>
  );
};
